import { RichTextField } from '@/components/admin/experiences/page-editor/rich-text-field';
import {
  NOYAU_TEXTS_IDS,
  noyauTextFieldIds,
} from '@/components/admin/reglages/noyau-texts-contracts';
import type { NoyauTextField } from '@/lib/noyau-texts-draft';
import { NOYAU_TEXT_FIELDS, filledCountOf } from '@/lib/noyau-texts-draft';
import { NOYAU_TEXT_FIELD_LABEL, cardCountLabel } from '@/lib/noyau-texts-labels';
import { cn } from '@/lib/utils';
import type { NoyauDefinition, NoyauTextEntryDraft, RichText } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * One noyau of Réglages > Noyaux: its three texts in one card (noyau.md §6.3,
 * maquette `ReglagesNoyauxParNoyau`, validated by the review of 2026-09-25).
 *
 * The NAME and the TRIPLET come from the table of `shared` (§2.1) and never
 * from the version, which carries neither (§6.2). The COLOUR is the official
 * one, painted by a 4 px rule across the top of the card and a dot beside the
 * name - the operator sees it, the person never does (N19). It arrives as a
 * value of the table and is applied inline: it is data of the method, not a
 * token of the charte, and no hex is written in this file.
 *
 * Each field is the Texte block's own WYSIWYG (`RichTextField`), in its mode
 * without variables: the texts are shared by every experience, so no variable
 * is in their reach, and the format and its renderer are the block's, which is
 * the point (§6.2). Its counter says « / 4000 », `EXPERIENCE_TEXT_MAX`.
 *
 * The counter is green at three out of three, grey otherwise - the maquette's
 * rule, and the only colour of the card that is not the noyau's.
 */

export interface NoyauCardProps {
  readonly noyau: NoyauDefinition;
  readonly entry: NoyauTextEntryDraft;
  /** A version opened by « Voir »: nothing is typable. */
  readonly readOnly: boolean;
  readonly onText: (field: NoyauTextField, text: RichText) => void;
}

export function NoyauCard({ noyau, entry, readOnly, onText }: NoyauCardProps) {
  const titleId = useId();
  const filled = filledCountOf(entry);
  const complete = filled === NOYAU_TEXT_FIELDS.length;

  return (
    <section
      aria-labelledby={titleId}
      data-testid={NOYAU_TEXTS_IDS.card}
      data-noyau={noyau.code}
      className="flex min-w-0 flex-col overflow-hidden rounded-lg border-2 border-gray-200 bg-white"
    >
      <div aria-hidden="true" className="h-1" style={{ backgroundColor: noyau.color }} />
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.75">
            <span
              aria-hidden="true"
              className="block size-3 shrink-0 rounded-full border border-noir/15"
              style={{ backgroundColor: noyau.color }}
            />
            <h4 id={titleId} className="truncate font-semibold text-gray-800 text-sm leading-4.5">
              {noyau.name}
            </h4>
            <span className="shrink-0 text-gray-500 text-xs leading-4">· {noyau.code}</span>
          </div>
          <span
            data-testid={NOYAU_TEXTS_IDS.cardCount}
            data-complete={complete ? 'true' : 'false'}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-pill px-2.25 py-0.5 font-semibold text-xs leading-4',
              complete ? 'bg-vert-tint text-vert-deep' : 'bg-gray-100 text-gray-700',
            )}
          >
            {cardCountLabel(filled)}
          </span>
        </div>

        {NOYAU_TEXT_FIELDS.map((field) => (
          <RichTextField
            key={field}
            label={NOYAU_TEXT_FIELD_LABEL[field]}
            value={entry[field]}
            readOnly={readOnly}
            testIds={noyauTextFieldIds(field)}
            onChange={(text) => onText(field, text)}
          />
        ))}
      </div>
    </section>
  );
}
